import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAddNewProduct } from "../../../lib/query/mutations/useProductMutations";
import { ArrowLeft, ShieldExclamation } from "react-bootstrap-icons";
import { useMultiAuthCheck } from "../../../utilities/authCheck";
import { useUserContext } from "../../../context/UserContext";
import { toast } from "../../../utilities/toast";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";
import { useFormDraft } from "../../../hooks/useFormDraft";

if (typeof document !== "undefined" && !document.getElementById("ap-styles")) {
  const s = document.createElement("style");
  s.id = "ap-styles";
  s.textContent = `
    @keyframes _ap_sk { 0%,100%{opacity:1}50%{opacity:.4} }
    ._ap_sk { animation: _ap_sk 1.8s ease-in-out infinite; background: rgba(60,60,67,0.08); }
    .ap-input { background: rgba(118,118,128,0.10) !important; border: 1px solid transparent !important; border-radius: 10px !important; padding: 11px 14px !important; font-size: 0.9375rem !important; color: #1c1c1e !important; transition: border-color .18s, box-shadow .18s !important; width: 100%; outline: none; font-family: inherit; box-sizing: border-box; }
    .ap-input:focus { border-color: #007AFF !important; box-shadow: 0 0 0 3px rgba(0,122,255,0.12) !important; background: rgba(118,118,128,0.08) !important; }
    .ap-input::placeholder { color: rgba(60,60,67,0.32) !important; }
    .ap-input.ap-invalid { border-color: #FF3B30 !important; box-shadow: 0 0 0 3px rgba(255,59,48,0.10) !important; }
  `;
  document.head.appendChild(s);
}

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif';
const PAGE = { minHeight: "100vh", backgroundColor: "#F2F2F7", fontFamily: FONT };

const Skel = ({ w, h, r = 8, style = {} }) => (
  <div className="_ap_sk" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

const FieldLabel = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} style={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(60,60,67,0.55)", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 7 }}>
    {children}
  </label>
);

const useFormState = (initialState = {}) => {
  const [formState, setFormState] = useState({ validated: false, loading: false, error: null, ...initialState });
  const setFormLoading = useCallback((loading) => setFormState((p) => ({ ...p, loading, error: null })), []);
  const setFormError   = useCallback((error)   => setFormState((p) => ({ ...p, loading: false, error })), []);
  const setValidated   = useCallback((v)        => setFormState((p) => ({ ...p, validated: v })), []);
  return { formState, setFormLoading, setFormError, setValidated };
};

const AddProductForm = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { isAuthorized, isLoading: authLoading, logout } = useMultiAuthCheck(["elite", "merchant"]);
  const { token } = useUserContext();
  const { formState, setFormError, setValidated } = useFormState();
  const { mutateAsync: addNewProduct, isPending: isSubmitting } = useAddNewProduct();

  const [formData, setFormData] = useState({
    name: "", price: 0, imageSrc: "", description: "", category: "", type: "", subType: "",
  });

  const { restoredDraft, clearDraft } = useFormDraft(`add-product:${storeId}`, formData);

  useEffect(() => {
    if (restoredDraft) setFormData(restoredDraft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = useCallback((field) => (e) => {
    const value = field === "price" ? Number(e.target.value) : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const resetFields = useCallback(() => {
    setFormData({ name: "", price: 0, imageSrc: "", description: "", category: "", type: "", subType: "" });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);
    if (!formData.name.trim() || !formData.category.trim() || !formData.description.trim()) {
      setFormError({ message: "Please fill in all required fields" });
      return;
    }
    try {
      await addNewProduct({ product: { ...formData, storeId }, token });
      toast.success("Product added successfully!");
      resetFields();
      clearDraft();
      navigate(-1);
    } catch (error) {
      console.error(error);
      toast.error("Failed to add product. Try again later.");
    }
  };

  if (authLoading) {
    return (
      <div style={PAGE}>
        <div style={{ maxWidth: 680, margin: "0 auto", padding: "28px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <Skel w={34} h={34} r={17} /><Skel w={130} h={22} r={7} />
          </div>
          <div style={{ backgroundColor: "#fff", borderRadius: 16, border: "1px solid rgba(60,60,67,.08)", padding: 20 }}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} style={{ marginBottom: 20 }}>
                <Skel w={60} h={11} r={5} style={{ marginBottom: 8 }} />
                <Skel w="100%" h={42} r={10} />
              </div>
            ))}
            <Skel w="100%" h={44} r={12} />
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div style={PAGE}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "80vh", padding: 32 }}>
          <div style={{ width: 60, height: 60, borderRadius: 18, backgroundColor: "rgba(255,149,0,.12)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
            <ShieldExclamation size={28} color="#FF9500" />
          </div>
          <h3 style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e", marginBottom: 6, textAlign: "center" }}>Access Restricted</h3>
          <p style={{ fontSize: "0.875rem", color: "rgba(60,60,67,.5)", textAlign: "center", marginBottom: 24 }}>Merchant or admin authentication required.</p>
          <button onClick={logout} style={{ padding: "11px 28px", borderRadius: 12, border: "none", backgroundColor: "#007AFF", color: "#fff", fontSize: "0.9375rem", fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>
            Sign In
          </button>
        </div>
      </div>
    );
  }

  const isInvalid = (field) => formState.validated && !formData[field]?.toString().trim();

  return (
    <div style={PAGE}>
      <Seo {...generateNoIndexSEO("Add Product")} />
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "28px 16px 48px" }}>

        {/* Back nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <button onClick={() => navigate(-1)} style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(60,60,67,.08)", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#1c1c1e", flexShrink: 0 }}>
            <ArrowLeft size={16} />
          </button>
          <h2 style={{ fontSize: "1.375rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.03em", margin: 0 }}>Add Product</h2>
        </div>

        <div style={{ backgroundColor: "#fff", borderRadius: 16, border: "1px solid rgba(60,60,67,.09)", padding: "20px 24px", boxShadow: "0 1px 3px rgba(0,0,0,.04)" }}>
          <form noValidate onSubmit={handleSubmit}>

            {formState.error && (
              <div style={{ backgroundColor: "rgba(255,59,48,.08)", borderRadius: 10, padding: "11px 14px", marginBottom: 20, fontSize: "0.875rem", color: "#FF3B30" }}>
                {formState.error.message}
              </div>
            )}

            {/* Name + Price */}
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <FieldLabel htmlFor="ap-name">Name</FieldLabel>
                <input
                  id="ap-name"
                  type="text"
                  className={`ap-input${isInvalid("name") ? " ap-invalid" : ""}`}
                  value={formData.name}
                  onChange={handleInputChange("name")}
                  placeholder="Product name"
                  required
                />
                {isInvalid("name") && <div style={{ fontSize: "0.75rem", color: "#FF3B30", marginTop: 4 }}>Please provide a name</div>}
              </div>
              <div>
                <FieldLabel htmlFor="ap-price">Price</FieldLabel>
                <input
                  id="ap-price"
                  type="number"
                  className={`ap-input${isInvalid("price") ? " ap-invalid" : ""}`}
                  value={formData.price}
                  onChange={handleInputChange("price")}
                  placeholder="KSH"
                  required
                />
                {isInvalid("price") && <div style={{ fontSize: "0.75rem", color: "#FF3B30", marginTop: 4 }}>Required</div>}
              </div>
            </div>

            {/* Image URL */}
            <div style={{ marginBottom: 16 }}>
              <FieldLabel htmlFor="ap-image">Image URL</FieldLabel>
              <input
                id="ap-image"
                type="text"
                className={`ap-input${isInvalid("imageSrc") ? " ap-invalid" : ""}`}
                value={formData.imageSrc}
                onChange={handleInputChange("imageSrc")}
                placeholder="https://..."
                required
              />
              {isInvalid("imageSrc") && <div style={{ fontSize: "0.75rem", color: "#FF3B30", marginTop: 4 }}>Please provide an image URL</div>}
            </div>

            {/* Description */}
            <div style={{ marginBottom: 16 }}>
              <FieldLabel htmlFor="ap-description">Description</FieldLabel>
              <textarea
                id="ap-description"
                className={`ap-input${isInvalid("description") ? " ap-invalid" : ""}`}
                value={formData.description}
                onChange={handleInputChange("description")}
                placeholder="Write a description..."
                rows={4}
                required
                style={{ resize: "vertical" }}
              />
              {isInvalid("description") && <div style={{ fontSize: "0.75rem", color: "#FF3B30", marginTop: 4 }}>Please provide a description</div>}
            </div>

            {/* Category / Type / Sub type */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 24 }}>
              <div>
                <FieldLabel htmlFor="ap-category">Category</FieldLabel>
                <input
                  id="ap-category"
                  type="text"
                  className={`ap-input${isInvalid("category") ? " ap-invalid" : ""}`}
                  value={formData.category}
                  onChange={handleInputChange("category")}
                  placeholder="Category"
                  required
                />
                {isInvalid("category") && <div style={{ fontSize: "0.75rem", color: "#FF3B30", marginTop: 4 }}>Please provide a category</div>}
              </div>
              <div>
                <FieldLabel htmlFor="ap-type">Type</FieldLabel>
                <input
                  id="ap-type"
                  type="text"
                  className="ap-input"
                  value={formData.type}
                  onChange={handleInputChange("type")}
                  placeholder="Type"
                />
              </div>
              <div>
                <FieldLabel htmlFor="ap-subtype">Sub type</FieldLabel>
                <input
                  id="ap-subtype"
                  type="text"
                  className="ap-input"
                  value={formData.subType}
                  onChange={handleInputChange("subType")}
                  placeholder="Sub type"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: "100%", padding: "13px", borderRadius: 12, border: "none",
                backgroundColor: isSubmitting ? "rgba(0,122,255,.5)" : "#007AFF",
                color: "#fff", fontSize: "0.9375rem", fontWeight: 600,
                cursor: isSubmitting ? "not-allowed" : "pointer",
                fontFamily: FONT, transition: "background-color .18s",
              }}
            >
              {isSubmitting ? "Adding Product…" : "Add Product"}
            </button>

          </form>
        </div>
      </div>
    </div>
  );
};

export default AddProductForm;
